import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../Icon";
import JobForm from "../../JobForm";
import api from "../../../utils/api";
import AdminChat from "../../Adminchat";
import { exportAppsExcel } from "../../../utils/exportHelpers";
import { STATUS_CFG as STATUS_CONFIG } from "../assets/Status";
import { fmtDate, initials } from "../../../utils/chatFormatters";
import { STYLES as EXTRA_STYLES } from "../AdminDashboardStyles";
import {
  XIcon,
  JsonIcon,
  ExcelIcon,
  HomeIcon,
  CheckIcon,
  InterviewIcon,
} from "../../../utils/Icons";
import {
  FilterBar,
  EditUserModal,
  useSortedData,
  formatJobTitles,
  EmployeeDetailModal,
  EditEmployeeModal,
  ApplicationDetailModal,
  AnalyticsTab,
} from "./AdminHelpers";
/* ── Status badge class map ─────────────────────────────────────────────────── */
const STATUS_BADGE = {
  pending: "badge-amber",
  reviewed: "badge-blue",
  accepted: "badge-green",
  rejected: "badge-red",
};

/* ── Cache ──────────────────────────────────────────────────────────────────── */
const CACHE = { data: null, ts: 0 };
const CACHE_TTL = 30_000;

/* ══ MAIN COMPONENT ══════════════════════════════════════════════════════════ */
export default function AdminDashboard({ user, logout, notify }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [detailApp, setDetailApp] = useState(null);
  const [editEmployee, setEditEmployee] = useState(null);
  const [detailEmployee, setDetailEmployee] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [interviewSending, setInterviewSending] = useState(null);
  // Track which application IDs have had invites successfully sent
  const [interviewSent, setInterviewSent] = useState(new Set());
  const [selectedApps, setSelectedApps] = useState(new Set());
  const [unreadMsgs, setUnreadMsgs] = useState(0);

  useEffect(() => {
    const fetchUnread = () =>
      api
        .get("/chat/team-unread-count")
        .then(({ data }) => setUnreadMsgs(data.count || 0))
        .catch(() => {});
    fetchUnread();
    const iv = setInterval(fetchUnread, 8000);
    return () => clearInterval(iv);
  }, []);

  const [appSearch, setAppSearch] = useState("");
  const [appStatus, setAppStatus] = useState("all");
  const [appJob, setAppJob] = useState("all");
  const [empSearch, setEmpSearch] = useState("");
  const [empFilter, setEmpFilter] = useState("all");
  const [userSearch, setUserSearch] = useState("");
  const [userRole, setUserRole] = useState("all");
  const [exporting, setExporting] = useState(null); // "excel" | "json" | null

  useEffect(() => {
    const id = "ql-admin-extra-styles";
    if (!document.getElementById(id)) {
      const tag = document.createElement("style");
      tag.id = id;
      tag.innerHTML = EXTRA_STYLES;
      document.head.appendChild(tag);
    }
  }, []);

  const fetchAll = useCallback(
    async (silent = false, force = false) => {
      if (!force && CACHE.data && Date.now() - CACHE.ts < CACHE_TTL) {
        const { jobs: j, applications: a, employees: e, users: u } = CACHE.data;
        setJobs(j);
        setApplications(a);
        setEmployees(e);
        setUsers(u);
        if (!silent) setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      else setRefreshing(true);
      try {
        const { data: dashboardData } = await api.get("/dashboard/admin");
        const data = {
          jobs: dashboardData.jobs || [],
          applications: dashboardData.applications || [],
          employees: dashboardData.employees || [],
          users: dashboardData.users || [],
        };
        CACHE.data = data;
        CACHE.ts = Date.now();
        setJobs(data.jobs);
        setApplications(data.applications);
        setEmployees(data.employees);
        setUsers(data.users);
      } catch (err) {
        notify(err.response?.data?.message || "Failed to load data.", "error");
      } finally {
        if (!silent) setLoading(false);
        else setRefreshing(false);
      }
    },
    [notify],
  );

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const close = (e) => {
      if (!e.target.closest(".sidebar") && !e.target.closest(".sidebar-toggle"))
        setSidebarOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [sidebarOpen]);

  const navigateTab = useCallback(
    (newTab) => {
      setSidebarOpen(false);
      if (newTab === tab) return;
      setTab(newTab);
      setSelectedApps(new Set());
      if (
        [
          "overview",
          "applications",
          "employees",
          "users",
          "analytics",
        ].includes(newTab)
      )
        fetchAll(true);
    },
    [tab, fetchAll],
  );

  /* ── Interview ───────────────────────────────────────────────────────────── */
  const sendInterview = useCallback(
    async (app) => {
      setInterviewSending(app._id);
      try {
        await api.post(`/applications/${app._id}/interview`);
        // Mark this app as "sent" so button changes to "Sent ✓"
        setInterviewSent((prev) => new Set([...prev, app._id]));
        notify(`Interview invitation sent to ${app.userName}!`);
      } catch (err) {
        notify(
          err.response?.data?.message || "Failed to send interview invite.",
          "error",
        );
      } finally {
        setInterviewSending(null);
      }
    },
    [notify],
  );

  /* ── Jobs ────────────────────────────────────────────────────────────────── */
  const [deleteJobId, setDeleteJobId] = useState(null);

  const deleteJob = useCallback(
    async (id) => {
      try {
        await api.delete(`/jobs/${id}`);
        setJobs((prev) => prev.filter((j) => j._id !== id));
        CACHE.ts = 0;
        notify("Job deleted.");
      } catch (err) {
        notify(err.response?.data?.message || "Failed to delete job.", "error");
      } finally {
        setDeleteJobId(null);
      }
    },
    [notify],
  );

  const toggleJobStatus = useCallback(
    async (job) => {
      try {
        const { data } = await api.put(`/jobs/${job._id}`, {
          active: !job.active,
        });
        setJobs((prev) => prev.map((j) => (j._id === data._id ? data : j)));
        CACHE.ts = 0;
        notify(`Job ${data.active ? "activated" : "paused"}.`);
      } catch (err) {
        notify(err.response?.data?.message || "Failed to update job.", "error");
      }
    },
    [notify],
  );

  /* ── Applications ────────────────────────────────────────────────────────── */
  const updateAppStatus = useCallback(
    async (appId, status) => {
      const { data } = await api.patch(`/applications/${appId}/status`, {
        status,
      });
      setApplications((prev) =>
        prev.map((a) => (a._id === data._id ? data : a)),
      );
      CACHE.ts = 0;
      notify(`Marked as ${status}.`);
      if (status === "accepted") fetchAll(true, true);
      return data;
    },
    [notify, fetchAll],
  );

  const bulkUpdateStatus = useCallback(
    async (status) => {
      if (selectedApps.size === 0) return;
      const ids = [...selectedApps];
      try {
        await Promise.all(
          ids.map((id) => api.patch(`/applications/${id}/status`, { status })),
        );
        setApplications((prev) =>
          prev.map((a) => (ids.includes(a._id) ? { ...a, status } : a)),
        );
        CACHE.ts = 0;
        setSelectedApps(new Set());
        notify(`${ids.length} application(s) marked as ${status}.`);
        if (status === "accepted") fetchAll(true, true);
      } catch {
        notify("Bulk update partially failed.", "error");
      }
    },
    [selectedApps, notify, fetchAll],
  );

  /* ── Export handlers ─────────────────────────────────────────────────────── */
  const handleExportExcel = useCallback(async () => {
    setExporting("excel");
    try {
      await exportAppsExcel({ status: appStatus, jobTitle: appJob });
      notify("Excel file downloaded.");
    } catch {
      notify("Excel export failed.", "error");
    } finally {
      setExporting(null);
    }
  }, [appStatus, appJob, notify]);

  /* ── Derived data ────────────────────────────────────────────────────────── */
  const allApplicationsSorted = useMemo(
    () =>
      [...applications].sort(
        (a, b) => new Date(b.appliedAt) - new Date(a.appliedAt),
      ),
    [applications],
  );
  const pendingApplications = useMemo(
    () => applications.filter((a) => a.status !== "accepted"),
    [applications],
  );
  const pendingCount = useMemo(
    () => applications.filter((a) => a.status === "pending").length,
    [applications],
  );
  const activeJobs = useMemo(() => jobs.filter((j) => j.active).length, [jobs]);

  const uniqueJobTitles = useMemo(
    () =>
      [
        ...new Set(
          allApplicationsSorted.map((a) => a.jobTitle).filter(Boolean),
        ),
      ].sort(),
    [allApplicationsSorted],
  );

  const filteredApplications = useMemo(() => {
    const q = appSearch.toLowerCase().trim();
    return allApplicationsSorted.filter((a) => {
      const ms =
        !q ||
        (a.userName || "").toLowerCase().includes(q) ||
        (a.userEmail || "").toLowerCase().includes(q);
      const mst = appStatus === "all" || a.status === appStatus;
      const mj = appJob === "all" || a.jobTitle === appJob;
      return ms && mst && mj;
    });
  }, [allApplicationsSorted, appSearch, appStatus, appJob]);

  const handleExportJSON = useCallback(() => {
    try {
      // Always export the filtered list — even if it's empty.
      // Falling back to `applications` would export everything when filters yield zero results.
      const blob = new Blob([JSON.stringify(filteredApplications, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `applications_${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);

      notify("JSON file downloaded.");
    } catch {
      notify("JSON export failed.", "error");
    }
  }, [filteredApplications, notify]);

  const filteredEmployees = useMemo(() => {
    const q = empSearch.toLowerCase().trim();
    return employees.filter((e) => {
      const jobTitles = formatJobTitles(e.jobTitle, "").toLowerCase();
      const ms =
        !q ||
        (e.name || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q) ||
        jobTitles.includes(q);
      const mf =
        empFilter === "all" ||
        (empFilter === "verified" && e.isVerified) ||
        (empFilter === "unverified" && !e.isVerified) ||
        (empFilter === "active" && e.discordActivityStatus === "active") ||
        (empFilter === "inactive" && e.discordActivityStatus === "inactive");
      return ms && mf;
    });
  }, [employees, empSearch, empFilter]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.toLowerCase().trim();
    return users.filter((u) => {
      const ms =
        !q ||
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q);
      const mr = userRole === "all" || u.role === userRole;
      return ms && mr;
    });
  }, [users, userSearch, userRole]);

  const { sorted: sortedApps, SortTh: AppSortTh } = useSortedData(
    filteredApplications,
    "appliedAt",
    "desc",
  );
  const { sorted: sortedEmps, SortTh: EmpSortTh } = useSortedData(
    filteredEmployees,
    "name",
  );
  const { sorted: sortedUsers, SortTh: UserSortTh } = useSortedData(
    filteredUsers,
    "createdAt",
    "desc",
  );

  useEffect(() => {
    const iv = setInterval(() => {
      if (CACHE.ts > 0 && Date.now() - CACHE.ts > CACHE_TTL)
        fetchAll(true, true);
    }, CACHE_TTL);
    return () => clearInterval(iv);
  }, [fetchAll]);

  const navItems = [
    { id: "overview", icon: "home", label: "Overview", badge: 0 },
    { id: "jobs", icon: "briefcase", label: "Jobs", badge: 0 },
    {
      id: "applications",
      icon: "eye",
      label: "Applications",
      badge: pendingCount,
    },
    { id: "employees", icon: "users", label: "Employees", badge: 0 },
    { id: "users", icon: "star", label: "Users", badge: users.length },
    { id: "analytics", icon: "eye", label: "Analytics", badge: 0 },
    { id: "chat", icon: "chat", label: "Messages", badge: unreadMsgs },
    { id: "settings", icon: "settings", label: "Settings", badge: 0 },
  ];

  const toggleApp = (id) =>
    setSelectedApps((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAllApps = () => {
    if (selectedApps.size === sortedApps.length) setSelectedApps(new Set());
    else setSelectedApps(new Set(sortedApps.map((a) => a._id)));
  };

  /* ── Interview button renderer ───────────────────────────────────────────── */
  const renderInterviewBtn = (a, isMobile = false) => {
    if (a.status !== "pending" && a.status !== "reviewed") {
      return (
        <span style={{ fontSize: ".72rem", color: "var(--text-3)" }}>—</span>
      );
    }
    // AFTER
    if (interviewSent.has(a._id) || a.interviewInviteSent) {
      return (
        <span className="interview-sent-btn">
          <CheckIcon /> Sent
        </span>
      );
    }
    return (
      <button
        className="btn btn-sm"
        style={{
          background:
            interviewSending === a._id
              ? "rgba(79,142,247,.15)"
              : "rgba(124,58,237,.12)",
          color: "#a78bfa",
          border: "1px solid rgba(124,58,237,.3)",
          whiteSpace: "nowrap",
          fontSize: isMobile ? ".72rem" : ".76rem",
          gap: 5,
        }}
        disabled={interviewSending === a._id}
        onClick={() => sendInterview(a)}
      >
        {interviewSending === a._id ? (
          <span className="auth-spinner" />
        ) : (
          <>
            <InterviewIcon /> Interview
          </>
        )}
      </button>
    );
  };

  return (
    <div className="dashboard">
      {/* ── Modals ── */}
      {detailApp && (
        <ApplicationDetailModal
          app={detailApp}
          notify={notify}
          onClose={() => setDetailApp(null)}
          onStatusChange={async (appId, status) => {
            const updated = await updateAppStatus(appId, status);
            if (updated) setDetailApp(updated);
          }}
        />
      )}

      {detailEmployee && (
        <EmployeeDetailModal
          employee={detailEmployee}
          onClose={() => setDetailEmployee(null)}
          onEdit={(emp) => setEditEmployee(emp)}
        />
      )}

      {editEmployee && (
        <EditEmployeeModal
          employee={editEmployee}
          onClose={() => setEditEmployee(null)}
          notify={notify}
          onSaved={(updated) =>
            setEmployees((prev) =>
              prev.map((e) =>
                e._id === updated._id ? { ...e, ...updated } : e,
              ),
            )
          }
          onDeleted={(id) =>
            setEmployees((prev) => prev.filter((e) => e._id !== id))
          }
        />
      )}

      {editUser && (
        <EditUserModal
          user={editUser}
          onClose={() => setEditUser(null)}
          notify={notify}
          onSaved={(updated) =>
            setUsers((prev) =>
              prev.map((u) =>
                (u._id || u.id) === (updated._id || updated.id)
                  ? { ...u, ...updated }
                  : u,
              ),
            )
          }
          onDeleted={(id) =>
            setUsers((prev) => prev.filter((u) => (u._id || u.id) !== id))
          }
        />
      )}

      {/* ── Sidebar ── */}
      <aside className={`sidebar${sidebarOpen ? " sidebar--open" : ""}`}>
        <div className="sidebar-top">
          <img
            src="/logo.png"
            alt="Quantum Logics"
            width="26"
            height="26"
            style={{ borderRadius: 6, objectFit: "contain" }}
          />
          <span className="sidebar-brand">Quantum Logics</span>
        </div>
        <div className="sidebar-section-label">Navigation</div>
        {navItems.map((item) => (
          <button
            key={item.id}
            className={`sidebar-btn${tab === item.id ? " active" : ""}`}
            onClick={() => navigateTab(item.id)}
          >
            <Icon name={item.icon} size={16} />
            {item.label}
            {item.badge > 0 && (
              <span className="sidebar-badge">{item.badge}</span>
            )}
          </button>
        ))}
        <div className="sidebar-footer">
          {/* Home button in sidebar */}
          <button
            className="dash-home-btn"
            style={{
              width: "100%",
              justifyContent: "center",
              marginBottom: ".625rem",
            }}
            onClick={() => navigate("/")}
          >
            <HomeIcon /> Go to Home
          </button>
          <div className="sidebar-user">
            <div className="sidebar-user-name">Admin</div>
            <div className="sidebar-user-email">{user.email}</div>
          </div>
          <button className="sidebar-btn sidebar-logout" onClick={logout}>
            <Icon name="logout" size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <main className="main-content">
        <div className="dash-topbar">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarOpen((o) => !o)}
          >
            <Icon name="menu" size={18} />
          </button>
          <span className="dash-topbar-title">
            {navItems.find((n) => n.id === tab)?.label ?? "Dashboard"}
          </span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginLeft: "auto",
            }}
          >
            {/* Home button in topbar */}
            <button className="dash-home-btn" onClick={() => navigate("/")}>
              <HomeIcon /> Home
            </button>
            <button className="sidebar-toggle" onClick={logout}>
              <Icon name="logout" size={16} />
            </button>
          </div>
        </div>

        {/* ── Top progress bar (non-blocking) ── */}
        {loading && <div className="dash-top-bar" />}

        <div key={tab} className="tab-content">
          {/* ══ OVERVIEW ══ */}
          {tab === "overview" && (
            <>
              <div className="page-header">
                <h1>Admin Overview</h1>
                <p>A high-level view of Quantum Logics hiring activity.</p>
              </div>
              <div className="stats-cards">
                {[
                  {
                    icon: "briefcase",
                    num: activeJobs,
                    label: "Active Jobs",
                    action: () => navigateTab("jobs"),
                  },
                  {
                    icon: "eye",
                    num: pendingApplications.length,
                    label: "Open Applications",
                    action: () => navigateTab("applications"),
                  },
                  {
                    icon: "clock",
                    num: pendingCount,
                    label: "Pending Review",
                    action: () => {
                      setAppStatus("pending");
                      navigateTab("applications");
                    },
                  },
                  {
                    icon: "users",
                    num: employees.length,
                    label: "Employees",
                    action: () => navigateTab("employees"),
                  },
                  {
                    icon: "star",
                    num: users.length,
                    label: "Registered Users",
                    action: () => navigateTab("users"),
                  },
                ].map(({ icon, num, label, action }) => (
                  <div
                    key={label}
                    className="stat-card"
                    onClick={action}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="stat-card-icon">
                      <Icon name={icon} size={16} />
                    </div>
                    <div className="stat-card-num">{num}</div>
                    <div className="stat-card-label">{label}</div>
                  </div>
                ))}
              </div>

              <div className="card">
                <div className="card-header-row">
                  <h3 className="card-title">Recent Applications</h3>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigateTab("applications")}
                  >
                    View all
                  </button>
                </div>
                {allApplicationsSorted.length === 0 ? (
                  <div className="empty-state">
                    <p>No applications yet.</p>
                  </div>
                ) : (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Applicant</th>
                          <th>Position</th>
                          <th>Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allApplicationsSorted.slice(0, 8).map((a) => (
                          <tr
                            key={a._id}
                            style={{ cursor: "pointer" }}
                            onClick={() => setDetailApp(a)}
                          >
                            <td>
                              <div className="td-user">
                                <div className="td-avatar">
                                  {initials(a.userName || a.userEmail)}
                                </div>
                                <div>
                                  <div className="td-name">{a.userName}</div>
                                  <div className="td-email">{a.userEmail}</div>
                                </div>
                              </div>
                            </td>
                            <td className="td-secondary">{a.jobTitle}</td>
                            <td className="td-secondary">
                              {fmtDate(a.appliedAt)}
                            </td>
                            <td>
                              <span
                                className={`badge ${STATUS_BADGE[a.status] || "badge-gray"}`}
                              >
                                {STATUS_CONFIG[a.status]?.label}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ══ JOBS ══ */}
          {tab === "jobs" && (
            <>
              <div className="page-header page-header--row">
                <div>
                  <h1>Manage Jobs</h1>
                  <p>Create, edit, and control job postings.</p>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setEditJob(null);
                    setShowJobForm(true);
                  }}
                >
                  <Icon name="plus" size={15} /> New Job
                </button>
              </div>

              {showJobForm && (
                <div style={{ marginBottom: "1.5rem" }}>
                  <JobForm
                    initial={editJob}
                    notify={notify}
                    onSave={(saved) => {
                      setJobs((prev) =>
                        editJob
                          ? prev.map((j) => (j._id === saved._id ? saved : j))
                          : [saved, ...prev],
                      );
                      setShowJobForm(false);
                      setEditJob(null);
                      CACHE.ts = 0;
                      notify(editJob ? "Job updated!" : "Job created!");
                    }}
                    onCancel={() => {
                      setShowJobForm(false);
                      setEditJob(null);
                    }}
                  />
                </div>
              )}

              <div className="jobs-admin-grid">
                {jobs.map((job) => (
                  <div key={job._id} className="jobs-admin-card">
                    <div className="jobs-admin-dept-row">
                      <span className="job-dept-tag">{job.department}</span>
                      <span
                        className={`badge ${job.active ? "badge-green" : "badge-gray"}`}
                        style={{ fontSize: ".68rem" }}
                      >
                        {job.active ? "Active" : "Paused"}
                      </span>
                    </div>
                    <h4 className="jobs-admin-title">{job.title}</h4>
                    <div className="jobs-admin-meta">
                      <span>
                        <Icon name="location" size={12} />
                        {job.location}
                      </span>
                      <span>
                        <Icon name="clock" size={12} />
                        {job.type}
                      </span>
                      <span>
                        <Icon name="users" size={12} />
                        {
                          applications.filter(
                            (a) =>
                              a.jobId === job._id ||
                              a.jobId === job._id?.toString(),
                          ).length
                        }{" "}
                        applicants
                      </span>
                    </div>

                    {deleteJobId === job._id ? (
                      <div
                        style={{
                          display: "flex",
                          gap: ".5rem",
                          alignItems: "center",
                          marginTop: ".75rem",
                          padding: ".5rem .75rem",
                          background: "rgba(239,68,68,.08)",
                          borderRadius: 8,
                          border: "1px solid rgba(239,68,68,.2)",
                        }}
                      >
                        <span
                          style={{
                            fontSize: ".78rem",
                            color: "var(--red)",
                            flex: 1,
                          }}
                        >
                          Delete this job?
                        </span>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setDeleteJobId(null)}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{
                            background: "var(--red)",
                            color: "#fff",
                            border: "none",
                          }}
                          onClick={() => deleteJob(job._id)}
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <div className="jobs-admin-actions">
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => toggleJobStatus(job)}
                        >
                          {job.active ? "Pause" : "Activate"}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            setEditJob(job);
                            setShowJobForm(true);
                            window.scrollTo(0, 0);
                          }}
                        >
                          <Icon name="eye" size={13} /> Edit
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{
                            background: "rgba(239,68,68,.1)",
                            color: "var(--red)",
                            border: "1px solid rgba(239,68,68,.2)",
                          }}
                          onClick={() => setDeleteJobId(job._id)}
                        >
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                {jobs.length === 0 && (
                  <div className="empty-state" style={{ gridColumn: "1/-1" }}>
                    <p>No jobs yet. Create your first posting!</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ══ APPLICATIONS ══ */}
          {tab === "applications" && (
            <>
              <div className="page-header">
                <h1>Applications</h1>
                <p>
                  Review incoming applications. Accepted candidates appear in
                  the Employees tab.
                </p>
              </div>

              {allApplicationsSorted.length === 0 ? (
                <div className="card empty-state">
                  <Icon name="briefcase" size={36} color="var(--text-3)" />
                  <p>No open applications.</p>
                </div>
              ) : (
                <div className="card">
                  <FilterBar
                    search={appSearch}
                    onSearch={setAppSearch}
                    pills={[
                      { value: "all", label: "All" },
                      { value: "pending", label: "Pending" },
                      { value: "reviewed", label: "Reviewed" },
                      { value: "accepted", label: "Accepted" },
                      { value: "rejected", label: "Rejected" },
                    ]}
                    activeFilter={appStatus}
                    onFilter={setAppStatus}
                    selects={[
                      {
                        value: appJob,
                        onChange: setAppJob,
                        options: [
                          { value: "all", label: "All Positions" },
                          ...uniqueJobTitles.map((t) => ({
                            value: t,
                            label: t,
                          })),
                        ],
                      },
                    ]}
                    total={allApplicationsSorted.length}
                    filtered={filteredApplications.length}
                    onClear={() => {
                      setAppSearch("");
                      setAppStatus("all");
                      setAppJob("all");
                    }}
                    onRefresh={() => fetchAll(true, true)}
                    refreshing={refreshing}
                  />

                  {/* ── Export bar ────────────────────────────────────────── */}
                  <div className="export-bar">
                    <span className="export-bar-label">Export</span>

                    {/* Excel */}
                    <button
                      className="export-btn export-btn-excel"
                      onClick={handleExportExcel}
                      disabled={!!exporting}
                      title="Download beautifully formatted .xlsx file"
                    >
                      <ExcelIcon />
                      {exporting === "excel" ? "Generating…" : "Excel (.xlsx)"}
                    </button>

                    {/* JSON */}
                    <button
                      className="export-btn export-btn-json"
                      onClick={handleExportJSON}
                      disabled={!!exporting}
                      title="Download raw JSON — useful for importing into other tools"
                    >
                      <JsonIcon />
                      JSON
                    </button>

                    <span className="export-note">
                      {filteredApplications.length ===
                      allApplicationsSorted.length
                        ? `All ${allApplicationsSorted.length} applications`
                        : `${filteredApplications.length} of ${allApplicationsSorted.length} (filtered)`}{" "}
                      · Excel respects active filters on the server
                    </span>
                  </div>

                  {selectedApps.size > 0 && (
                    <div className="bulk-toolbar">
                      <span className="bulk-count">
                        {selectedApps.size} selected
                      </span>
                      {["reviewed", "accepted", "rejected"].map((s) => (
                        <button
                          key={s}
                          className="btn btn-ghost btn-sm"
                          onClick={() => bulkUpdateStatus(s)}
                        >
                          Mark {STATUS_CONFIG[s].label}
                        </button>
                      ))}
                      <span className="bulk-spacer" />
                      <button
                        className="ql-clear"
                        onClick={() => setSelectedApps(new Set())}
                      >
                        <XIcon /> Deselect
                      </button>
                    </div>
                  )}

                  {sortedApps.length === 0 ? (
                    <div
                      className="empty-state"
                      style={{ padding: "2.5rem 1rem" }}
                    >
                      <p>No applications match your filters.</p>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ marginTop: 8 }}
                        onClick={() => {
                          setAppSearch("");
                          setAppStatus("all");
                          setAppJob("all");
                        }}
                      >
                        Clear filters
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="table-wrap">
                        <table>
                          <thead>
                            <tr>
                              <th style={{ width: 32 }}>
                                <input
                                  type="checkbox"
                                  checked={
                                    selectedApps.size === sortedApps.length &&
                                    sortedApps.length > 0
                                  }
                                  onChange={toggleAllApps}
                                  style={{ cursor: "pointer" }}
                                />
                              </th>
                              <AppSortTh col="userName" label="Applicant" />
                              <AppSortTh col="jobTitle" label="Position" />
                              <AppSortTh col="experience" label="Exp." />
                              <AppSortTh col="appliedAt" label="Date" />
                              <AppSortTh col="status" label="Status" />
                              <th>Update</th>
                              <th>Interview</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedApps.map((a) => (
                              <tr
                                key={a._id}
                                className={
                                  selectedApps.has(a._id) ? "row-selected" : ""
                                }
                              >
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedApps.has(a._id)}
                                    onChange={() => toggleApp(a._id)}
                                    style={{ cursor: "pointer" }}
                                  />
                                </td>
                                <td>
                                  <div className="td-user">
                                    <div className="td-avatar">
                                      {initials(a.userName || a.userEmail)}
                                    </div>
                                    <div>
                                      <div className="td-name">
                                        {a.userName}
                                      </div>
                                      <div className="td-email">
                                        {a.userEmail}
                                      </div>
                                      {a.phone && (
                                        <div className="td-email">
                                          {a.phone}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="td-secondary">{a.jobTitle}</td>
                                <td className="td-secondary">
                                  {a.experience || "—"}
                                </td>
                                <td className="td-secondary">
                                  {fmtDate(a.appliedAt)}
                                </td>
                                <td>
                                  <span
                                    className={`badge ${STATUS_BADGE[a.status] || "badge-gray"}`}
                                  >
                                    {STATUS_CONFIG[a.status]?.label}
                                  </span>
                                </td>
                                <td>
                                  <select
                                    className="form-select dash-select"
                                    value={a.status}
                                    onChange={(e) =>
                                      updateAppStatus(
                                        a._id,
                                        e.target.value,
                                      ).catch(() => {})
                                    }
                                  >
                                    <option value="pending">Pending</option>
                                    <option value="reviewed">Reviewed</option>
                                    <option value="accepted">Accepted</option>
                                    <option value="rejected">Rejected</option>
                                  </select>
                                </td>
                                <td>{renderInterviewBtn(a)}</td>
                                <td>
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => setDetailApp(a)}
                                  >
                                    <Icon name="eye" size={13} /> More
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="app-list">
                        {sortedApps.map((a) => (
                          <div key={a._id} className="app-list-item">
                            <div className="app-list-avatar">
                              {initials(a.userName || a.userEmail)}
                            </div>
                            <div className="app-list-info">
                              <div className="app-list-name">
                                {a.userName || a.userEmail}
                              </div>
                              <div className="app-list-sub">
                                {a.jobTitle}
                                {a.experience ? ` · ${a.experience}` : ""}
                              </div>
                            </div>
                            <div
                              className="app-list-right"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 4,
                              }}
                            >
                              <select
                                className="form-select"
                                style={{
                                  padding: "3px 6px",
                                  fontSize: ".72rem",
                                  borderRadius: "7px",
                                }}
                                value={a.status}
                                onChange={(e) =>
                                  updateAppStatus(a._id, e.target.value).catch(
                                    () => {},
                                  )
                                }
                              >
                                <option value="pending">Pending</option>
                                <option value="reviewed">Reviewed</option>
                                <option value="accepted">Accepted</option>
                                <option value="rejected">Rejected</option>
                              </select>
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setDetailApp(a)}
                                style={{ fontSize: ".72rem" }}
                              >
                                More Detail
                              </button>
                              {renderInterviewBtn(a, true)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* ══ EMPLOYEES ══ */}
          {tab === "employees" && (
            <>
              <div className="page-header">
                <h1>Employees</h1>
                <p>
                  Users with accepted applications. Verified means they've
                  completed their Logic Signature.
                </p>
              </div>

              {employees.length === 0 ? (
                <div className="card empty-state">
                  <Icon name="users" size={36} color="var(--text-3)" />
                  <p>No employees yet. Accept applications to add employees.</p>
                </div>
              ) : (
                <div className="card">
                  <FilterBar
                    search={empSearch}
                    onSearch={setEmpSearch}
                    pills={[
                      { value: "all", label: "All" },
                      { value: "verified", label: "✓ Verified" },
                      { value: "unverified", label: "Unverified" },
                      { value: "active", label: "🟢 Active" },
                      { value: "inactive", label: "🔴 Inactive" },
                    ]}
                    activeFilter={empFilter}
                    onFilter={setEmpFilter}
                    total={employees.length}
                    filtered={filteredEmployees.length}
                    onClear={() => {
                      setEmpSearch("");
                      setEmpFilter("all");
                    }}
                    onRefresh={() => fetchAll(true, true)}
                    refreshing={refreshing}
                  />

                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <EmpSortTh col="name" label="Employee" />
                          <EmpSortTh col="jobTitle" label="Role" />
                          <EmpSortTh
                            col="discordActivityStatus"
                            label="Discord Activity"
                          />
                          <EmpSortTh col="isVerified" label="Verified" />
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedEmps.map((u) => (
                          <tr
                            key={u._id}
                            style={{ cursor: "pointer" }}
                            onClick={() => setDetailEmployee(u)}
                          >
                            <td>
                              <div className="td-user">
                                <div className="td-avatar">
                                  {initials(u.name || u.email)}
                                </div>
                                <div>
                                  <div className="td-name">{u.name}</div>
                                  <div className="td-email">
                                    {u.discordUrl || "—"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="td-secondary">
                              {formatJobTitles(u.jobTitle)}
                            </td>
                            <td>
                              {u.discordActivityStatus === "active" ? (
                                <span className="badge badge-green">
                                  🟢 Active
                                </span>
                              ) : u.discordActivityStatus === "inactive" ? (
                                <span className="badge badge-red">
                                  🔴 Inactive
                                </span>
                              ) : (
                                <span className="badge badge-gray">—</span>
                              )}
                            </td>
                            <td>
                              {u.isVerified ? (
                                <span className="badge badge-green">
                                  Verified
                                </span>
                              ) : (
                                <span className="badge badge-gray">
                                  Unverified
                                </span>
                              )}
                            </td>
                            <td>
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDetailEmployee(u);
                                }}
                              >
                                More Info
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="app-list">
                    {sortedEmps.map((u) => (
                      <div
                        key={u._id}
                        className="app-list-item"
                        style={{ cursor: "pointer" }}
                        onClick={() => setDetailEmployee(u)}
                      >
                        <div className="app-list-avatar">
                          {initials(u.name || u.email)}
                        </div>
                        <div className="app-list-info">
                          <div className="app-list-name">{u.name}</div>
                          <div className="app-list-sub">
                            {u.discordUrl || "—"} · {formatJobTitles(u.jobTitle)}
                          </div>
                        </div>
                        <div
                          className="app-list-right"
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: 5,
                          }}
                        >
                          {u.discordActivityStatus === "active" ? (
                            <span className="badge badge-green">🟢 Active</span>
                          ) : u.discordActivityStatus === "inactive" ? (
                            <span className="badge badge-red">🔴 Inactive</span>
                          ) : null}
                          {u.isVerified ? (
                            <span className="badge badge-green">Verified</span>
                          ) : (
                            <span className="badge badge-gray">Unverified</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ══ USERS ══ */}
          {tab === "users" && (
            <>
              <div className="page-header">
                <h1>Users</h1>
                <p>All registered accounts. Click Edit to manage a user.</p>
              </div>

              <div className="card">
                <FilterBar
                  search={userSearch}
                  onSearch={setUserSearch}
                  pills={[
                    { value: "all", label: "All" },
                    { value: "user", label: "Users" },
                    { value: "interviewer", label: "Interviewers" },
                  ]}
                  activeFilter={userRole}
                  onFilter={setUserRole}
                  total={users.length}
                  filtered={filteredUsers.length}
                  onClear={() => {
                    setUserSearch("");
                    setUserRole("all");
                  }}
                  onRefresh={() => fetchAll(true, true)}
                  refreshing={refreshing}
                />

                {users.length === 0 ? (
                  <div className="empty-state">
                    <Icon name="users" size={36} color="var(--text-3)" />
                    <p>No registered users.</p>
                  </div>
                ) : (
                  <>
                    <div className="table-wrap">
                      <table>
                        <thead>
                          <tr>
                            <UserSortTh col="name" label="User" />
                            <UserSortTh col="email" label="Email" />
                            <UserSortTh col="role" label="Role" />
                            <th>Verified</th>
                            <UserSortTh col="createdAt" label="Joined" />
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedUsers.map((u) => (
                            <tr key={u._id || u.id}>
                              <td>
                                <div className="td-user">
                                  <div
                                    className="td-avatar"
                                    style={{
                                      background:
                                        u.role === "admin"
                                          ? "linear-gradient(135deg,#f59e0b,#d97706)"
                                          : u.role === "interviewer"
                                            ? "linear-gradient(135deg,#7c3aed,#a78bfa)"
                                            : "linear-gradient(135deg,var(--blue),#818cf8)",
                                    }}
                                  >
                                    {initials(u.name || u.email)}
                                  </div>
                                  <div>
                                    <div className="td-name">{u.name}</div>
                                    {u.jobTitle && (
                                      <div className="td-email">
                                        {u.jobTitle}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="td-secondary">{u.email}</td>
                              <td>
                                <span
                                  className={`badge ${u.role === "admin" ? "badge-orange" : u.role === "interviewer" ? "badge-purple" : "badge-blue"}`}
                                >
                                  {u.role === "interviewer"
                                    ? "🎙️ Interviewer"
                                    : u.role}
                                </span>
                              </td>
                              <td>
                                {u.isVerified ? (
                                  <span className="badge badge-green">
                                    Verified
                                  </span>
                                ) : (
                                  <span className="badge badge-gray">—</span>
                                )}
                              </td>
                              <td className="td-secondary">
                                {fmtDate(u.createdAt)}
                              </td>
                              <td>
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => setEditUser(u)}
                                >
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="app-list">
                      {sortedUsers.map((u) => (
                        <div key={u._id || u.id} className="app-list-item">
                          <div
                            className="app-list-avatar"
                            style={{
                              background:
                                u.role === "admin"
                                  ? "linear-gradient(135deg,#f59e0b,#d97706)"
                                  : u.role === "interviewer"
                                    ? "linear-gradient(135deg,#7c3aed,#a78bfa)"
                                    : "linear-gradient(135deg,var(--blue),#818cf8)",
                            }}
                          >
                            {initials(u.name || u.email)}
                          </div>
                          <div className="app-list-info">
                            <div className="app-list-name">
                              {u.name || u.email}
                            </div>
                            <div className="app-list-sub">
                              {u.email} · {u.role}
                            </div>
                          </div>
                          <div
                            className="app-list-right"
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 5,
                            }}
                          >
                            <span
                              className={`badge ${u.role === "admin" ? "badge-orange" : u.role === "interviewer" ? "badge-purple" : "badge-blue"}`}
                            >
                              {u.role === "interviewer"
                                ? "🎙️ Interviewer"
                                : u.role}
                            </span>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => setEditUser(u)}
                              style={{ fontSize: ".72rem" }}
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* ══ ANALYTICS ══ */}
          {tab === "analytics" && (
            <AnalyticsTab
              jobs={jobs}
              applications={applications}
              employees={employees}
              users={users}
            />
          )}

          {/* ══ CHAT ══ */}
          {tab === "chat" && <AdminChat admin={user} />}

          {/* ══ SETTINGS ══ */}
          {tab === "settings" && (
            <>
              <div className="page-header">
                <h1>Settings</h1>
                <p>Platform configuration and deployment info.</p>
              </div>
              <div className="card settings-card">
                <div className="settings-card-icon">
                  <Icon name="settings" size={22} />
                </div>
                <div>
                  <h3 className="settings-card-title">Email (Gmail SMTP)</h3>
                  <p className="settings-card-desc">
                    To enable email notifications, configure your Gmail App
                    Password in <code>backend/.env</code>.
                  </p>
                  <ol
                    className="settings-list"
                    style={{
                      margin: ".625rem 0 0 1.25rem",
                      fontSize: ".84rem",
                      color: "var(--text-2)",
                      lineHeight: 1.9,
                    }}
                  >
                    <li>
                      Open <strong>Google Account → Security</strong>
                    </li>
                    <li>
                      Enable <strong>2-Step Verification</strong>
                    </li>
                    <li>
                      Search for <strong>App Passwords</strong>
                    </li>
                    <li>Create one for "Mail" — copy the 16-character code</li>
                    <li>
                      Paste into <code>backend/.env</code> as{" "}
                      <code>EMAIL_PASS</code>
                    </li>
                  </ol>
                </div>
              </div>
              <div className="card settings-card">
                <div className="settings-card-icon">
                  <Icon name="quantum" size={22} />
                </div>
                <div>
                  <h3 className="settings-card-title">Platform Info</h3>
                  <p className="settings-card-desc">
                    Current deployment details for your Quantum Logics instance.
                  </p>
                  <div className="settings-info-grid">
                    {[
                      { label: "Frontend", value: "quantumlogicslimited.com" },
                      { label: "API", value: "Vercel Serverless" },
                      { label: "Database", value: "MongoDB Atlas" },
                      { label: "Auth", value: "JWT + bcrypt" },
                    ].map((r) => (
                      <div key={r.label} className="settings-info-row">
                        <span className="settings-info-label">{r.label}</span>
                        <span className="settings-info-value">{r.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="card settings-card">
                <div className="settings-card-icon">
                  <Icon name="users" size={20} color="var(--green)" />
                </div>
                <div>
                  <h3 className="settings-card-title">Cache</h3>
                  <p className="settings-card-desc">
                    Data is cached for 30 seconds to reduce API calls. Force a
                    fresh fetch anytime.
                  </p>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ marginTop: ".5rem" }}
                    onClick={() => {
                      CACHE.ts = 0;
                      fetchAll(true, true);
                      notify("Cache cleared — data refreshed.");
                    }}
                  >
                    Clear cache &amp; refresh
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* ── Bottom nav ── */}
      <nav className="dash-bottom-nav">
        {navItems.map((item) => (
          <button
            key={item.id}
            className={`dash-bottom-btn${tab === item.id ? " active" : ""}`}
            onClick={() => navigateTab(item.id)}
          >
            <span className="dash-bottom-icon">
              <Icon name={item.icon} size={19} />
              {item.badge > 0 && (
                <span className="dash-bottom-badge">{item.badge}</span>
              )}
            </span>
            <span className="dash-bottom-label">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
